import { useEffect, useRef, useState, useCallback } from 'react';
import Media from 'react-media';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { Box, Textfield, useTheme } from '@simplywallst/ui-core';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';

import { currencyToSymbol } from '@/utilities/formatting';

interface Props {
  companyCurrencyISO: string;
  userFairValue: number | null;
  onUpdate: (value: number | null) => void;
  error: string | undefined;
}

const MyValuationInput = ({
  companyCurrencyISO,
  error,
  onUpdate,
  userFairValue,
}: Props) => {
  const { isMobile } = useUserAgent();
  const theme = useTheme();

  const input = useRef<HTMLInputElement>(null);
  const [fairValue, setFairValue] = useState<string>('');

  useEffect(() => {
    if (userFairValue === null) {
      setFairValue('');
      return;
    }
    const value = String(userFairValue);
    if (/^([0-9]+([.][0-9]*)?|[.][0-9]+)$/g.test(value)) {
      setFairValue(value);
    }
  }, [userFairValue]);

  const handleChange = useCallback(() => {
    if (input.current !== null) {
      const value = input.current.value;
      if (value.length === 0) {
        setFairValue('');
      } else if (/^([0-9]+([.][0-9]*)?|[.][0-9]+)$/g.test(value)) {
        setFairValue(value);
      }
    }
  }, [input]);

  const handleUpdate = useCallback(() => {
    if (input.current !== null) {
      onUpdate(
        input.current.value ? Number.parseFloat(input.current.value) : null
      );
    }
  }, [input, onUpdate]);

  const companyCurrencySymbol: string | undefined =
    currencyToSymbol[companyCurrencyISO]?.currency.symbol;

  return (
    <Media
      queries={{ mobile: { maxWidth: theme.xsMaxRaw } }}
      defaultMatches={{ mobile: isMobile }}
    >
      {(matches) => {
        return (
          <Box maxWidth={matches.mobile ? '120px' : '180px'}>
            <Textfield
              ref={input}
              prefix={companyCurrencySymbol}
              postfix={<EditSolid opacity={0.5} />}
              styleType="standard"
              name="narrative-fair-value-modal-user-defined-input"
              type="text"
              placeholder="0.00"
              value={fairValue}
              onBlur={handleUpdate}
              onChange={handleChange}
              error={error}
            />
          </Box>
        );
      }}
    </Media>
  );
};

export default MyValuationInput;
